import { endOfMonth, startOfMonth } from 'date-fns'
import { useMemo } from 'react'

import { useGetEventsForTrackedGamesQuery, useGetTrackedEventsByGameQuery } from '../../../api/core'
import { useCurrentMarket } from '../../markets'
import { GameTopIAPs } from '../../top-IAP/types/GameTopIAPs'
import { CalendarTimeRanges } from '../types/Calendar'
import { EventsDataFetchingMode } from '../types/EventsDataFetchingMode'
import { EstimateValue, LiveEventReview, PerformanceChange, TrackingEventByGame, TrackingEventsByGame } from '../types/TrackingEvents'

type UseLiveEventsByCalendarTimeRangesParams = {
  calendarTimeRanges: CalendarTimeRanges
  trackedGameIds: string[]
  fallbackDateFrom?: number
  fallbackDateTo?: number
  feedTimeFrom?: number
  feedTimeTo?: number
  dataFetchingMode?: EventsDataFetchingMode
}

type GameTimeRange = {
  startTimestamp: number
  endTimestamp: number
}

type TrackedEventsResult = {
  events: TrackingEventByGame
  performanceChanges: { [gameId: string]: PerformanceChange[] }
  performanceValues: { [gameId: string]: EstimateValue[] }
  versions: { [gameId: string]: Array<{ appId: number; versionReleaseDate: number; version: string }> }
  featuresData: NonNullable<TrackingEventsByGame['featureData']>
  topIAPs: { [gameId: string]: GameTopIAPs[] }
  comments: { [gameId: string]: LiveEventReview[] }
  stats: TrackingEventsByGame['stats']
  isLoading: boolean
  isFetching: boolean
  isError: boolean
}

// Constants for empty states
const EMPTY_TRACKED_EVENTS_DATA = {
  events: {},
  performanceChanges: {},
  performanceValues: {},
  versions: {},
  featuresData: {},
  topIAPs: {},
  comments: {},
  stats: {},
} as const

const EMPTY_TRACKED_EVENTS_RESULT: TrackedEventsResult = {
  ...EMPTY_TRACKED_EVENTS_DATA,
  isLoading: false,
  isFetching: false,
  isError: false,
}

/**
 * Hook for fetching tracked events for given games based on visible calendar time ranges
 * Resolves time range for each game separately and stores them in a map where game id is the key.
 * Fetches events for each game individually using game-specific time ranges.
 * Falls back to provided fallback dates if no calendar ranges exist.
 * All timestamps are rounded to month boundaries (start of month for start, end of month for end).
 * @param dataFetchingMode - Specifies how events data should be fetched
 */
export const useLiveEventsByCalendarTimeRanges = ({
  calendarTimeRanges,
  trackedGameIds,
  fallbackDateFrom,
  fallbackDateTo,
  feedTimeFrom,
  feedTimeTo,
  dataFetchingMode = EventsDataFetchingMode.PerGame,
}: UseLiveEventsByCalendarTimeRangesParams) => {
  const { currentMarketIso } = useCurrentMarket()

  // Call all data fetching hooks unconditionally (hooks rules compliance)
  // Each hook will skip its queries when not active
  const perGameData = usePerGameEventsData(
    calendarTimeRanges,
    trackedGameIds,
    fallbackDateFrom,
    fallbackDateTo,
    currentMarketIso,
    dataFetchingMode === EventsDataFetchingMode.PerGame
  )
  const eventCalendarData = useEventCalendarEventsData(
    calendarTimeRanges,
    trackedGameIds,
    fallbackDateFrom,
    fallbackDateTo,
    currentMarketIso,
    dataFetchingMode === EventsDataFetchingMode.EventCalendar
  )
  const feedData = useFeedEventsData(trackedGameIds, feedTimeFrom, feedTimeTo, currentMarketIso, dataFetchingMode === EventsDataFetchingMode.Feed)
  const eventStatsData = useEventStatsEventsData(
    trackedGameIds,
    fallbackDateFrom,
    fallbackDateTo,
    currentMarketIso,
    dataFetchingMode === EventsDataFetchingMode.EventStats
  )

  // Note: Individual query errors are handled by their respective RTK Query hooks

  // Select the appropriate data source based on mode
  const activeDataSource = useMemo(() => {
    const dataSourceMap = {
      [EventsDataFetchingMode.PerGame]: perGameData,
      [EventsDataFetchingMode.EventCalendar]: eventCalendarData,
      [EventsDataFetchingMode.Feed]: feedData,
      [EventsDataFetchingMode.EventStats]: eventStatsData,
    } as const
    return dataSourceMap[dataFetchingMode] ?? dataSourceMap[EventsDataFetchingMode.PerGame]
  }, [dataFetchingMode, perGameData, eventCalendarData, feedData, eventStatsData])

  const { data: selectedData, isLoading, isFetching, isError } = activeDataSource

  // Calculate gameTimeRangesMap for PerGame mode (needed for return value)
  const gameTimeRangesMap = useMemo(() => {
    if (dataFetchingMode !== EventsDataFetchingMode.PerGame) {
      return {}
    }

    return Object.fromEntries(
      trackedGameIds
        .map((gameId) => {
          const resolvedRange = resolveTimeRange(calendarTimeRanges[gameId], fallbackDateFrom, fallbackDateTo)
          return resolvedRange ? [gameId, resolvedRange] : null
        })
        .filter((entry): entry is [string, GameTimeRange] => entry !== null)
    )
  }, [dataFetchingMode, calendarTimeRanges, trackedGameIds, fallbackDateFrom, fallbackDateTo])

  // Return selected data with loading/error states
  return {
    ...(selectedData ?? { ...EMPTY_TRACKED_EVENTS_RESULT, isLoading, isFetching, isError }),
    isLoading,
    isFetching,
    isError,
    gameTimeRangesMap,
  }
}

/**
 * Transforms query data to TrackedEventsResult format
 */
const transformQueryDataToResult = (
  queryData: TrackingEventsByGame | undefined,
  isLoading: boolean,
  isFetching: boolean,
  isError: boolean
): TrackedEventsResult => ({
  events: queryData?.events ?? {},
  performanceChanges: (queryData?.performanceChanges ?? {}) as { [gameId: string]: PerformanceChange[] },
  performanceValues: (queryData?.performanceValues ?? {}) as { [gameId: string]: EstimateValue[] },
  versions: (queryData?.versions ?? {}) as { [gameId: string]: Array<{ appId: number; versionReleaseDate: number; version: string }> },
  featuresData: queryData?.featureData ?? {},
  topIAPs: (queryData?.topIAPs ?? {}) as { [gameId: string]: GameTopIAPs[] },
  comments: queryData?.comments ?? {},
  stats: queryData?.stats ?? {},
  isLoading,
  isFetching,
  isError,
})

/**
 * Creates a standardized query result object
 */
const createQueryResult = <T>(data: T | null, isLoading: boolean, isFetching: boolean, isError: boolean) => ({
  data,
  isLoading,
  isFetching,
  isError,
})

/**
 * Rounds timestamps to month boundaries
 * @param startTimestamp - Timestamp to round down to start of month
 * @param endTimestamp - Timestamp to round up to end of month
 * @returns Object with startTimestamp at start of month and endTimestamp at end of month
 */
const roundToMonthBoundaries = (startTimestamp: number, endTimestamp: number): GameTimeRange => ({
  startTimestamp: startOfMonth(new Date(startTimestamp)).getTime(),
  endTimestamp: endOfMonth(new Date(endTimestamp)).getTime(),
})

/**
 * Resolves time range from calendar range or fallback dates
 * Applies buffer and rounds to month boundaries
 */
const resolveTimeRange = (
  calendarRange: { timeStart?: number; timeEnd?: number } | undefined,
  fallbackDateFrom?: number,
  fallbackDateTo?: number,
  buffer: number = 6
): GameTimeRange | null => {
  const { timeStart, timeEnd } = calendarRange ?? {}

  // Use calendar range if available
  if (timeStart !== undefined && timeEnd !== undefined) {
    const visibleRange = timeEnd - timeStart
    const rangeBuffer = buffer * visibleRange
    return roundToMonthBoundaries(timeStart - rangeBuffer, timeEnd + rangeBuffer)
  }

  // Fallback to provided dates if no calendar range exists
  if (fallbackDateFrom !== undefined && fallbackDateTo !== undefined) {
    const fallbackVisibleRange = fallbackDateTo - fallbackDateFrom
    const rangeBuffer = buffer * fallbackVisibleRange
    return roundToMonthBoundaries(fallbackDateFrom - rangeBuffer, fallbackDateTo + rangeBuffer)
  }

  // Last resort: use fallback dates or zeros
  const start = fallbackDateFrom ?? 0
  const end = fallbackDateTo ?? 0
  return start && end ? roundToMonthBoundaries(start, end) : { startTimestamp: start, endTimestamp: end }
}

/**
 * Merges array of TrackingEventsByGame responses into a single object
 */
const mergeGameResponses = (responses: TrackingEventsByGame[]): TrackedEventsResult => {
  if (responses.length === 0) {
    return EMPTY_TRACKED_EVENTS_RESULT
  }

  const mergedData = responses.reduce(
    (
      acc: {
        events: TrackingEventByGame
        performanceChanges: { [gameId: string]: PerformanceChange[] }
        performanceValues: { [gameId: string]: EstimateValue[] }
        versions: { [gameId: string]: Array<{ appId: number; versionReleaseDate: number; version: string }> }
        featureData: NonNullable<TrackingEventsByGame['featureData']>
        topIAPs: { [gameId: string]: GameTopIAPs[] }
        comments: { [gameId: string]: LiveEventReview[] }
        stats: TrackingEventsByGame['stats']
      },
      gameData
    ) => {
      // Merge events
      Object.entries(gameData.events ?? {}).forEach(([gameId, events]) => {
        acc.events[gameId] = [...(acc.events[gameId] ?? []), ...events]
      })

      // Merge arrays (performanceChanges, performanceValues, versions, topIAPs, comments)
      Object.entries(gameData.performanceChanges ?? {}).forEach(([gameId, changes]) => {
        if (changes) {
          acc.performanceChanges[gameId] = [...(acc.performanceChanges[gameId] ?? []), ...changes]
        }
      })
      Object.entries(gameData.performanceValues ?? {}).forEach(([gameId, values]) => {
        if (values) {
          acc.performanceValues[gameId] = [...(acc.performanceValues[gameId] ?? []), ...values]
        }
      })
      Object.entries(gameData.versions ?? {}).forEach(([gameId, versions]) => {
        if (versions) {
          acc.versions[gameId] = [...(acc.versions[gameId] ?? []), ...versions]
        }
      })
      Object.entries(gameData.topIAPs ?? {}).forEach(([gameId, topIAPs]) => {
        if (topIAPs) {
          acc.topIAPs[gameId] = [...(acc.topIAPs[gameId] ?? []), ...topIAPs]
        }
      })
      Object.entries(gameData.comments ?? {}).forEach(([gameId, comments]) => {
        if (comments) {
          acc.comments[gameId] = [...(acc.comments[gameId] ?? []), ...comments]
        }
      })

      // Merge featureData (objects, not arrays)
      if (gameData.featureData) {
        Object.entries(gameData.featureData).forEach(([gameId, featureData]) => {
          if (featureData) {
            acc.featureData[gameId] = { ...(acc.featureData[gameId] ?? {}), ...featureData }
          }
        })
      }

      // Merge stats (shallow merge per game)
      if (gameData.stats) {
        acc.stats = { ...acc.stats, ...gameData.stats }
      }

      return acc
    },
    {
      events: {} as TrackingEventByGame,
      performanceChanges: {} as { [gameId: string]: PerformanceChange[] },
      performanceValues: {} as { [gameId: string]: EstimateValue[] },
      versions: {} as { [gameId: string]: Array<{ appId: number; versionReleaseDate: number; version: string }> },
      featureData: {} as NonNullable<TrackingEventsByGame['featureData']>,
      topIAPs: {} as { [gameId: string]: GameTopIAPs[] },
      comments: {} as { [gameId: string]: LiveEventReview[] },
      stats: {} as TrackingEventsByGame['stats'],
    }
  )

  return {
    events: mergedData.events,
    performanceChanges: mergedData.performanceChanges as { [gameId: string]: PerformanceChange[] },
    performanceValues: mergedData.performanceValues as { [gameId: string]: EstimateValue[] },
    versions: mergedData.versions as { [gameId: string]: Array<{ appId: number; versionReleaseDate: number; version: string }> },
    featuresData: mergedData.featureData as NonNullable<TrackingEventsByGame['featureData']>,
    topIAPs: mergedData.topIAPs as { [gameId: string]: GameTopIAPs[] },
    comments: mergedData.comments as { [gameId: string]: LiveEventReview[] },
    stats: mergedData.stats,
    isLoading: false,
    isFetching: false,
    isError: false,
  }
}

/**
 * Hook for fetching events using per-game time ranges
 * Each game has its own time range resolved from calendar or fallback dates
 */
const usePerGameEventsData = (
  calendarTimeRanges: CalendarTimeRanges,
  trackedGameIds: string[],
  fallbackDateFrom: number | undefined,
  fallbackDateTo: number | undefined,
  currentMarketIso: string,
  isActive: boolean
): { data: TrackedEventsResult | null; isLoading: boolean; isFetching: boolean; isError: boolean } => {
  // Resolve time range for each game separately and store them in a map
  const gameTimeRangesMap = useMemo(() => {
    return Object.fromEntries(
      trackedGameIds
        .map((gameId) => {
          const resolvedRange = resolveTimeRange(calendarTimeRanges[gameId], fallbackDateFrom, fallbackDateTo)
          return resolvedRange ? [gameId, resolvedRange] : null
        })
        .filter((entry): entry is [string, GameTimeRange] => entry !== null)
    )
  }, [calendarTimeRanges, trackedGameIds, fallbackDateFrom, fallbackDateTo])

  // Build query parameters array for useGetTrackedEventsByGameQuery
  const queryParams = useMemo(() => {
    if (!isActive) return []
    return trackedGameIds
      .map((gameId) => {
        const timeRange = gameTimeRangesMap[gameId]
        if (timeRange?.startTimestamp !== undefined && timeRange?.endTimestamp !== undefined) {
          return {
            gameId,
            startTimestamp: timeRange.startTimestamp,
            endTimestamp: timeRange.endTimestamp,
            marketIso: currentMarketIso,
          }
        }
        return null
      })
      .filter((param): param is NonNullable<typeof param> => param !== null)
  }, [trackedGameIds, gameTimeRangesMap, currentMarketIso, isActive])

  const trackedEventsQuery = useGetTrackedEventsByGameQuery(queryParams, {
    skip: !isActive || queryParams.length === 0,
  })

  const result = useMemo(() => {
    if (!isActive) {
      return createQueryResult<TrackedEventsResult | null>(null, false, false, false)
    }

    const { data, isLoading, isFetching, isError } = trackedEventsQuery

    if (!data?.length) {
      return createQueryResult(transformQueryDataToResult(undefined, isLoading, isFetching, isError), isLoading, isFetching, isError)
    }

    const mergedData = mergeGameResponses(data)
    return createQueryResult({ ...mergedData, isLoading, isFetching, isError }, isLoading, isFetching, isError)
  }, [isActive, trackedEventsQuery])

  return result
}

/**
 * Hook for fetching events using eventCalendar time range for all games
 * All games share the same time range from the eventCalendar
 */
const useEventCalendarEventsData = (
  calendarTimeRanges: CalendarTimeRanges,
  trackedGameIds: string[],
  fallbackDateFrom: number | undefined,
  fallbackDateTo: number | undefined,
  currentMarketIso: string,
  isActive: boolean
): { data: TrackedEventsResult | null; isLoading: boolean; isFetching: boolean; isError: boolean } => {
  const eventCalendarRange = calendarTimeRanges['eventCalendar']
  const shouldUse =
    (isActive && eventCalendarRange?.timeStart !== undefined && eventCalendarRange?.timeEnd !== undefined) ||
    (fallbackDateFrom !== undefined && fallbackDateTo !== undefined)

  // Resolve time range for eventCalendar
  const eventCalendarTimeRange = useMemo(
    () => resolveTimeRange(eventCalendarRange, fallbackDateFrom, fallbackDateTo),
    [eventCalendarRange, fallbackDateFrom, fallbackDateTo]
  )

  const eventCalendarQuery = useGetEventsForTrackedGamesQuery(
    {
      gameIds: trackedGameIds,
      startTimestamp: eventCalendarTimeRange?.startTimestamp ?? 0,
      endTimestamp: eventCalendarTimeRange?.endTimestamp ?? 0,
      marketIso: currentMarketIso,
    },
    {
      skip: !shouldUse || !eventCalendarTimeRange || trackedGameIds.length === 0,
    }
  )

  const result = useMemo(() => {
    const { data, isLoading, isFetching, isError } = eventCalendarQuery

    if (!shouldUse || !data) {
      return createQueryResult<TrackedEventsResult | null>(null, isLoading, isFetching, isError)
    }

    return createQueryResult(transformQueryDataToResult(data, isLoading, isFetching, isError), isLoading, isFetching, isError)
  }, [shouldUse, eventCalendarQuery])

  return result
}

/**
 * Hook for fetching events for Live Event Feed tab
 * Uses feedTimeFrom and feedTimeTo directly without buffering or adjustment
 */
const useFeedEventsData = (
  trackedGameIds: string[],
  feedTimeFrom: number | undefined,
  feedTimeTo: number | undefined,
  currentMarketIso: string,
  isActive: boolean
): { data: TrackedEventsResult | null; isLoading: boolean; isFetching: boolean; isError: boolean } => {
  const feedQuery = useGetEventsForTrackedGamesQuery(
    {
      gameIds: trackedGameIds,
      startTimestamp: feedTimeFrom ?? 0,
      endTimestamp: feedTimeTo ?? 0,
      marketIso: currentMarketIso,
    },
    {
      skip: !isActive || trackedGameIds.length === 0 || feedTimeFrom === undefined || feedTimeTo === undefined,
    }
  )

  const result = useMemo(() => {
    const { data, isLoading, isFetching, isError } = feedQuery

    if (!isActive || !data) {
      return createQueryResult<TrackedEventsResult | null>(null, isLoading, isFetching, isError)
    }

    return createQueryResult(transformQueryDataToResult(data, isLoading, isFetching, isError), isLoading, isFetching, isError)
  }, [isActive, feedQuery])

  return result
}

/**
 * Hook for fetching events for Event Statistics tab.
 * Resolves the effective time range from timeFrom/timeTo using resolveTimeRange with a buffer of 1,
 * then fetches events via useGetEventsForTrackedGamesQuery for the resolved range.
 */
const useEventStatsEventsData = (
  trackedGameIds: string[],
  timeFrom: number | undefined,
  timeTo: number | undefined,
  currentMarketIso: string,
  isActive: boolean
): { data: TrackedEventsResult | null; isLoading: boolean; isFetching: boolean; isError: boolean } => {
  const timeRange = resolveTimeRange({ timeStart: timeFrom, timeEnd: timeTo }, timeFrom, timeTo, 1)
  const feedQuery = useGetEventsForTrackedGamesQuery(
    {
      gameIds: trackedGameIds,
      startTimestamp: timeRange?.startTimestamp ?? 0,
      endTimestamp: timeRange?.endTimestamp ?? 0,
      marketIso: currentMarketIso,
    },
    {
      skip: !isActive || trackedGameIds.length === 0 || timeFrom === undefined || timeTo === undefined,
    }
  )

  const result = useMemo(() => {
    const { data, isLoading, isFetching, isError } = feedQuery

    if (!isActive || !data) {
      return createQueryResult<TrackedEventsResult | null>(null, isLoading, isFetching, isError)
    }

    return createQueryResult(transformQueryDataToResult(data, isLoading, isFetching, isError), isLoading, isFetching, isError)
  }, [isActive, feedQuery])

  return result
}
